import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import repeat from '~/common/utils/repeat';

function useWindowGridVirtualizer({
  gap,
  minColumnWidth,
  itemsLength,
  hasNextPage,
  minColumns = 0,
  maxColumns,
  estimatedSize = 30,
  overscan = 5,
}: {
  gap: number;
  minColumnWidth: number;
  itemsLength: number;
  hasNextPage: boolean;
  minColumns?: number;
  maxColumns: number;
  estimatedSize?: number;
  overscan?: number;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const [itemsPerRow, setItemsPerRow] = useState(1);
  const [itemWidth, setItemWidth] = useState('100%');
  const [isListRefPresent, setIsListRefPresent] = useState(false);

  const getItemsPerRow = useCallback(() => {
    if (!listRef.current) {
      return;
    }

    const { width } = listRef.current.getBoundingClientRect();

    const getAvailableSpace = (cols: number): number => {
      const gaps = (cols - 1) * gap;
      return width - gaps;
    };

    const result =
      repeat(maxColumns - minColumns, (i) => maxColumns - i).find((value) => {
        const availableSpace = getAvailableSpace(value);
        return availableSpace / value >= minColumnWidth;
      }) || 1;
    setItemsPerRow(result);
    setItemWidth(result === 1 ? '100%' : `${getAvailableSpace(result) / result}px`);
    // Items length needs to be there in case state updates with new data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gap, minColumnWidth, itemsLength, listRef.current, isListRefPresent]);

  const handleListRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setIsListRefPresent(true);
    } else {
      setIsListRefPresent(false);
    }

    listRef.current = node;
  }, []);

  useLayoutEffect(() => {
    getItemsPerRow();
    window.addEventListener('resize', getItemsPerRow);

    return () => {
      window.removeEventListener('resize', getItemsPerRow);
    };
  }, [getItemsPerRow]);

  const rowsCount = useMemo(() => Math.ceil(itemsLength / itemsPerRow), [itemsLength, itemsPerRow]);

  const virtualizer = useWindowVirtualizer({
    count: hasNextPage ? rowsCount + 1 : rowsCount,
    estimateSize: () => estimatedSize,
    overscan,
    scrollMargin: listRef.current?.offsetTop ?? 0,
    gap,
  });

  return {
    virtualizer,
    itemWidth,
    itemsPerRow,
    listRef: handleListRef,
    rowsCount,
  };
}

export default useWindowGridVirtualizer;
